import httpClient from 'services/httpClient';

export interface OverviewFilters {
  startDate?: Date | null;
  endDate?: Date | null;
  preset?: string | null;
  areaID?: string | null;
}

export interface AssessmentScheduleOverview {
  msAssessmentScheduleID: string;
  name: string;
  type: string;
  programID: string;
  programName: string;
  startTime: string;
  endTime: string;
  status: 'Planned' | 'On Going' | 'Completed' | 'Incomplete';
  totalParticipants: number;
  totalRespondents: number;
  completedParticipants: number;
  completedRespondents: number;
  isBeforeSchedule: boolean;
  previousScheduleID: string | null;
  progressPercentage: number;
  daysRemaining: number;
  completionRate: number;
  createdDate: string;
  lastActivity: string;
  notificationDate: string | null;
}

export interface OverviewStats {
  [x: string]: string | number;
  totalAssessments: number;
  plannedCount: number;
  onGoingCount: number;
  completedCount: number;
  incompleteCount: number;
  totalParticipants: number;
  totalRespondents: number;
  completedParticipants: number;
  completedRespondents: number;
  totalCompletedRespondents: number;
  totalCompletedParticipants: number;
  totalTypeProduct: number;
  totalTypeNonProduct: number;
}

export interface ComparisonAnalysis {
  beforeAssessmentID: string;
  afterAssessmentID: string;
  beforeAssessmentName: string;
  afterAssessmentName: string;
  improvementRate: number;
  participantComparison: Array<{
    participantID: string;
    beforeScore: number;
    afterScore: number;
    improvement: number;
  }>;
  moduleComparison: Array<{
    moduleID: string;
    moduleName: string;
    beforeAverage: number;
    afterAverage: number;
    improvement: number;
  }>;
  skalaComparison?: Array<{
    skalaCategory: string;
    beforeAverage: number;
    afterAverage: number;
    improvement: number;
    beforeResponseCount: number;
    afterResponseCount: number;
  }>;
  beforeSkalaDetails?: Array<{
    skalaCategory: string;
    averageScore: number;
    responseCount: number;
  }>;
  afterSkalaDetails?: Array<{
    skalaCategory: string;
    averageScore: number;
    responseCount: number;
  }>;
}

export interface OverviewData {
  stats: OverviewStats;
  recentAssessments: AssessmentScheduleOverview[];
  upcomingAssessments: AssessmentScheduleOverview[];
  ongoingAssessments: AssessmentScheduleOverview[];
  completedAssessments: AssessmentScheduleOverview[];
  incompleteAssessments: AssessmentScheduleOverview[];
  comparisonAnalyses: ComparisonAnalysis[];
  nonProductAssessments: AssessmentScheduleOverview[];
  productAssessments: AssessmentScheduleOverview[];
}

class OverviewService {
  private getDefaultOverviewResponse(): OverviewData {
    return {
      stats: {
        totalAssessments: 0,
        plannedCount: 0,
        onGoingCount: 0,
        completedCount: 0,
        incompleteCount: 0,
        totalParticipants: 0,
        totalRespondents: 0,
        completedParticipants: 0,
        completedRespondents: 0,
        totalTypeProduct: 0,
        totalTypeNonProduct: 0,
        totalCompletedRespondents: 0,
        totalCompletedParticipants: 0
      },
      recentAssessments: [],
      upcomingAssessments: [],
      ongoingAssessments: [],
      completedAssessments: [],
      incompleteAssessments: [],
      comparisonAnalyses: [],
      nonProductAssessments: [],
      productAssessments: []
    };
  }

  private getMockOverviewData(): OverviewData {
    return {
      stats: {
        totalAssessments: 25,
        plannedCount: 5,
        onGoingCount: 8,
        completedCount: 10,
        incompleteCount: 2,
        totalParticipants: 150,
        totalRespondents: 450,
        completedParticipants: 120,
        completedRespondents: 380,
        totalTypeProduct: 15,
        totalTypeNonProduct: 10,
        totalCompletedRespondents: 380,
        totalCompletedParticipants: 120
      },
      recentAssessments: [
        {
          msAssessmentScheduleID: 'uuid-1',
          name: 'Product Development Assessment Q1',
          type: 'product',
          programID: 'PROG-001',
          programName: 'Product Development Program',
          startTime: '2024-01-15T09:00:00Z',
          endTime: '2024-01-30T17:00:00Z',
          status: 'Completed',
          totalParticipants: 12,
          totalRespondents: 36,
          completedParticipants: 10,
          completedRespondents: 36,
          isBeforeSchedule: true,
          previousScheduleID: null,
          progressPercentage: 100,
          daysRemaining: 0,
          completionRate: 100,
          createdDate: '2024-01-01T10:00:00Z',
          lastActivity: '2024-01-30T16:45:00Z',
          notificationDate: '2024-01-25T09:00:00Z'
        }
      ],
      upcomingAssessments: [
        {
          msAssessmentScheduleID: 'uuid-2',
          name: 'Leadership Assessment Q2',
          type: 'non-product',
          programID: 'PROG-002',
          programName: 'Leadership Development',
          startTime: '2024-02-15T09:00:00Z',
          endTime: '2024-02-28T17:00:00Z',
          status: 'Planned',
          totalParticipants: 8,
          totalRespondents: 24,
          completedParticipants: 0,
          completedRespondents: 36,
          isBeforeSchedule: true,
          previousScheduleID: null,
          progressPercentage: 0,
          daysRemaining: 10,
          completionRate: 0,
          createdDate: '2024-01-20T10:00:00Z',
          lastActivity: '2024-01-20T10:00:00Z',
          notificationDate: '2024-02-10T09:00:00Z'
        }
      ],
      ongoingAssessments: [
        {
          msAssessmentScheduleID: 'uuid-3',
          name: 'Technical Skills Assessment',
          type: 'product',
          programID: 'PROG-003',
          programName: 'Technical Excellence Program',
          startTime: '2024-01-20T09:00:00Z',
          endTime: '2024-02-10T17:00:00Z',
          status: 'On Going',
          totalParticipants: 15,
          totalRespondents: 45,
          completedParticipants: 12,
          completedRespondents: 36,
          isBeforeSchedule: true,
          previousScheduleID: null,
          progressPercentage: 71.1,
          daysRemaining: 5,
          completionRate: 71.1,
          createdDate: '2024-01-10T10:00:00Z',
          lastActivity: '2024-02-05T14:30:00Z',
          notificationDate: '2024-02-05T09:00:00Z'
        }
      ],
      completedAssessments: [
        {
          msAssessmentScheduleID: 'uuid-1',
          name: 'Product Development Assessment Q1',
          type: 'product',
          programID: 'PROG-001',
          programName: 'Product Development Program',
          startTime: '2024-01-15T09:00:00Z',
          endTime: '2024-01-30T17:00:00Z',
          status: 'Completed',
          totalParticipants: 12,
          totalRespondents: 36,
          completedParticipants: 12,
          completedRespondents: 36,
          isBeforeSchedule: true,
          previousScheduleID: null,
          progressPercentage: 100,
          daysRemaining: 0,
          completionRate: 100,
          createdDate: '2024-01-01T10:00:00Z',
          lastActivity: '2024-01-30T16:45:00Z',
          notificationDate: '2024-01-25T09:00:00Z'
        }
      ],
      incompleteAssessments: [
        {
          msAssessmentScheduleID: 'uuid-4',
          name: 'Communication Skills Assessment',
          type: 'non-product',
          programID: 'PROG-004',
          programName: 'Communication Excellence',
          startTime: '2024-01-01T09:00:00Z',
          endTime: '2024-01-15T17:00:00Z',
          status: 'Incomplete',
          totalParticipants: 10,
          totalRespondents: 30,
          completedParticipants: 8,
          completedRespondents: 36,
          isBeforeSchedule: true,
          previousScheduleID: null,
          progressPercentage: 73.3,
          daysRemaining: -5,
          completionRate: 73.3,
          createdDate: '2023-12-15T10:00:00Z',
          lastActivity: '2024-01-14T16:20:00Z',
          notificationDate: '2024-01-10T09:00:00Z'
        }
      ],
      comparisonAnalyses: [
        {
          beforeAssessmentID: 'uuid-5',
          afterAssessmentID: 'uuid-6',
          beforeAssessmentName: 'Initial Technical Assessment',
          afterAssessmentName: 'Follow-up Technical Assessment',
          improvementRate: 15.5,
          participantComparison: [
            {
              participantID: 'BINUS001',
              beforeScore: 75,
              afterScore: 85,
              improvement: 10
            }
          ],
          moduleComparison: [
            {
              moduleID: 'MOD-001',
              moduleName: 'Programming Fundamentals',
              beforeAverage: 78.5,
              afterAverage: 87.2,
              improvement: 8.7
            }
          ]
        }
      ],
      nonProductAssessments: [],
      productAssessments: []
    };
  }

  private validateAndTransformData(data: any): OverviewData {
    const defaultResponse = this.getDefaultOverviewResponse();

    try {
      if (!data || typeof data !== 'object') {
        return defaultResponse;
      }

      const stats: OverviewStats = {
        totalAssessments: data.stats?.totalAssessments ?? 0,
        plannedCount: data.stats?.plannedCount ?? 0,
        onGoingCount: data.stats?.onGoingCount ?? 0,
        completedCount: data.stats?.completedCount ?? 0,
        incompleteCount: data.stats?.incompleteCount ?? 0,
        totalParticipants: data.stats?.totalParticipants ?? 0,
        totalRespondents: data.stats?.totalRespondents ?? 0,
        completedParticipants: data.stats?.completedParticipants ?? 0,
        completedRespondents: data.stats?.completedRespondents ?? 0,
        totalTypeProduct: data.stats?.totalTypeProduct ?? 0,
        totalTypeNonProduct: data.stats?.totalTypeNonProduct ?? 0,
        totalCompletedRespondents: data.stats?.totalCompletedRespondents ?? 0,
        totalCompletedParticipants: data.stats?.totalCompletedParticipants ?? 0
      };

      const result: OverviewData = {
        stats,
        recentAssessments: Array.isArray(data.recentAssessments) ? data.recentAssessments : [],
        upcomingAssessments: Array.isArray(data.upcomingAssessments)
          ? data.upcomingAssessments
          : [],
        ongoingAssessments: Array.isArray(data.ongoingAssessments) ? data.ongoingAssessments : [],
        completedAssessments: Array.isArray(data.completedAssessments)
          ? data.completedAssessments
          : [],
        incompleteAssessments: Array.isArray(data.incompleteAssessments)
          ? data.incompleteAssessments
          : [],
        comparisonAnalyses: Array.isArray(data.comparisonAnalyses) ? data.comparisonAnalyses : [],
        nonProductAssessments: Array.isArray(data.nonProductAssessments)
          ? data.nonProductAssessments
          : [],
        productAssessments: Array.isArray(data.productAssessments) ? data.productAssessments : []
      };

      return result;
    } catch {
      return defaultResponse;
    }
  }

  private formatDateToYYYYMMDD(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  async getOverview(filters: OverviewFilters = {}): Promise<OverviewData> {
    try {
      const params = new URLSearchParams();

      if (filters.startDate) {
        params.append('startDate', this.formatDateToYYYYMMDD(filters.startDate));
      }
      if (filters.endDate) {
        params.append('endDate', this.formatDateToYYYYMMDD(filters.endDate));
      }
      if (filters.areaID) {
        params.append('areaID', filters.areaID);
      }

      const queryString = params.toString();
      const url = `/lia/overview${queryString ? `?${queryString}` : ''}`;

      const response = await httpClient.get(url);

      let data = response.data?.data || response.data;

      if (Array.isArray(data)) {
        data = data[0];
      }

      const transformedData = this.validateAndTransformData(data);

      return transformedData;
    } catch (error) {
      const isAxiosError = error && typeof error === 'object' && 'response' in error;
      const isNetworkError = error && typeof error === 'object' && 'code' in error;

      if (
        (isAxiosError && (error as any).response?.status === 404) ||
        (isNetworkError && (error as any).code === 'ERR_NETWORK')
      ) {
        return this.getMockOverviewData();
      }

      return this.getDefaultOverviewResponse();
    }
  }

  async getAssessmentsByStatus(
    status: 'Planned' | 'On Going' | 'Completed' | 'Incomplete',
    filters: OverviewFilters = {},
    page: number = 1,
    limit: number = 10,
    search?: string
  ): Promise<{
    assessments: AssessmentScheduleOverview[];
    totalPages: number;
    totalRecords: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (search) params.append('search', search);
      if (filters.startDate)
        params.append('startDate', this.formatDateToYYYYMMDD(filters.startDate));
      if (filters.endDate) params.append('endDate', this.formatDateToYYYYMMDD(filters.endDate));
      if (filters.areaID) params.append('areaID', filters.areaID);

      const response = await httpClient.get(`/lia/assessments/status/${status}`, {
        params
      });

      const result = response.data;

      return {
        assessments: result.data || [],
        totalPages: result.totalPages || 1,
        totalRecords: result.totalRecords || 0
      };
    } catch {
      return {
        assessments: [],
        totalPages: 1,
        totalRecords: 0
      };
    }
  }

  async getAssessmentComparison(beforeId: string, afterId: string): Promise<ComparisonAnalysis> {
    try {
      const response = await httpClient.get(`/lia/assessments/comparison/${beforeId}/${afterId}`);

      const result = response.data;
      return result.data;
    } catch {
      return {
        beforeAssessmentID: beforeId,
        afterAssessmentID: afterId,
        beforeAssessmentName: 'Assessment (Before)',
        afterAssessmentName: 'Assessment (After)',
        improvementRate: 0,
        participantComparison: [],
        moduleComparison: []
      };
    }
  }

  async refreshAssessmentStatus(assessmentId: string): Promise<{
    status: string;
    totalParticipants: number;
    totalRespondents: number;
    completedParticipants: number;
    completedRespondents: number;
    notificationDate: string | null;
  }> {
    try {
      const response = await httpClient.post(`/lia/assessments/${assessmentId}/refresh-status`);

      const result = response.data;
      return result.data;
    } catch {
      return {
        status: 'Unknown',
        totalParticipants: 0,
        totalRespondents: 0,
        completedParticipants: 0,
        completedRespondents: 0,
        notificationDate: null
      };
    }
  }

  async downloadAssessments(
    filters: OverviewFilters = {},
    downloadOptions: {
      status?: 'All' | 'Planned' | 'On Going' | 'Completed' | 'Incomplete';
      type?: 'Product' | 'Non-Product' | null;
      filename?: string;
    } = {}
  ): Promise<void> {
    try {
      const params = new URLSearchParams();

      // Add filter parameters
      if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
      if (filters.areaID) params.append('areaID', filters.areaID);

      // Add download-specific parameters
      if (downloadOptions.status && downloadOptions.status !== 'All') {
        params.append('status', downloadOptions.status);
      }
      if (downloadOptions.type) {
        params.append('type', downloadOptions.type);
      }

      const queryString = params.toString();
      const url = `/lia/assessments/download${queryString ? `?${queryString}` : ''}`;

      const response = await httpClient.get(url, { responseType: 'blob' });

      // Handle file download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Use custom filename if provided
      const defaultFilename = `assessments_${downloadOptions.status || 'all'}_${downloadOptions.type || 'all'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = downloadOptions.filename || defaultFilename;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      throw new Error('Failed to download assessments');
    }
  }

  async getAssessmentAnalytics(assessmentId: string): Promise<{
    assessmentID: string;
    name: string;
    type: string;
    status: string;
    notificationDate: string | null;
    participantAnalytics: {
      totalParticipants: number;
      completedParticipants: number;
      pendingParticipants: number;
      averageScore: number;
      scoreDistribution: any[];
    };
    respondentAnalytics: {
      totalRespondents: number;
      completedRespondents: number;
      pendingRespondents: number;
      responseRate: number;
      averageResponseTime: number;
    };
    moduleAnalytics: Array<{
      moduleID: string;
      moduleName: string;
      averageScore: number;
      responseCount: number;
      completionRate: number;
    }>;
    skalaAnalytics: Array<{
      skalaCategory: string;
      averageScore: number;
      responseCount: number;
      minScore: number;
      maxScore: number;
    }>;
  }> {
    try {
      const response = await httpClient.get(`/lia/assessments/${assessmentId}/analytics`);
      const result = response.data;
      return result.data;
    } catch {
      return {
        assessmentID: assessmentId,
        name: 'Unknown Assessment',
        type: 'Unknown',
        status: 'Unknown',
        notificationDate: null,
        participantAnalytics: {
          totalParticipants: 0,
          completedParticipants: 0,
          pendingParticipants: 0,
          averageScore: 0,
          scoreDistribution: []
        },
        respondentAnalytics: {
          totalRespondents: 0,
          completedRespondents: 0,
          pendingRespondents: 0,
          responseRate: 0,
          averageResponseTime: 0
        },
        moduleAnalytics: [],
        skalaAnalytics: []
      };
    }
  }
}

export const overviewService = new OverviewService();
